import { motion } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-20 md:pb-32 overflow-hidden"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        {/* Meta row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-between mb-14 md:mb-24 text-xs font-mono tracking-widest uppercase text-zinc-500"
        >
          <span data-testid="hero-tag">[ EST. 2019 — INDEPENDENT AGENCY ]</span>
          <span className="hidden md:inline">SYS/01 — CREATIVE OPS</span>
          <span className="hidden md:inline">↘ SCROLL</span>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end"
        >
          {/* Left: Massive H1 */}
          <div className="lg:col-span-8">
            <motion.h1
              variants={item}
              data-testid="hero-title"
              className="font-display font-black uppercase tracking-tighter leading-[0.85] text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9rem] xl:text-[10.5rem]"
            >
              We slide
              <br />
              brands into
              <br />
              <span className="inline-block relative">
                <span className="text-[#002FA7]">motion.</span>
                <span className="absolute -right-4 top-2 md:top-6 w-3 h-3 md:w-5 md:h-5 bg-[#002FA7] animate-pulse" />
              </span>
            </motion.h1>

            <motion.p
              variants={item}
              data-testid="hero-subtitle"
              className="mt-10 max-w-xl text-base md:text-lg text-zinc-600 leading-relaxed"
            >
              Slide Ventures is a creative &amp; performance marketing agency
              engineered for modern brands. Strategy, storytelling and paid
              growth — under one roof, obsessed with velocity.
            </motion.p>

            <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#002FA7] text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-[#001B6B] transition-colors duration-300"
              >
                Start a project
                <ArrowDownRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
              </a>
              <a
                href="#work"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border-2 border-zinc-950 px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors duration-300"
              >
                See our work
              </a>
            </motion.div>
          </div>

          {/* Right: Asymmetric image + meta card */}
          <motion.div variants={item} className="lg:col-span-4 lg:pl-4">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1493397212122-2b85dda8106b?auto=format&fit=crop&w=1200&q=80"
                alt="Slide Ventures — abstract architecture"
                className="w-full aspect-[4/5] object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-zinc-200 p-5 max-w-[240px] shadow-[8px_8px_0_0_#09090b]">
                <div className="flex items-center gap-2 mb-2 text-[10px] font-mono uppercase tracking-widest text-[#002FA7]">
                  <Sparkles className="w-3 h-3" /> Now booking
                </div>
                <div className="font-display font-bold uppercase text-lg leading-tight">
                  Q1 / 2026 <br /> slots open.
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 gap-6">
              <div data-testid="hero-stat-1">
                <div className="font-display font-black text-4xl md:text-5xl">
                  120<span className="text-[#002FA7]">+</span>
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">
                  brands scaled
                </div>
              </div>
              <div data-testid="hero-stat-2">
                <div className="font-display font-black text-4xl md:text-5xl">
                  $48M
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">
                  ad spend managed
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
